import {MessageService} from 'primeng/api';
import {Message} from 'primeng/api/message';
import {Toast} from 'primeng/toast';

import {inject, Injectable, Injector} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '@shared-types';

import {ToastConfig, ToastType} from './data';

@Injectable({
	providedIn: 'root',
	deps: [MessageService, TranslateService],
})
export class ToastService {
	private static readonly DEFAULT_TOAST_LIFE: number = 3000;
	private static readonly EXTENDED_TOAST_LIFE: number = 5000;
	/* -------------------------------------------------- */
	private readonly messageService: MessageService = inject(MessageService);
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly injector: Injector = inject(Injector);
	public toastRef: Toast;

	/* -------------------------------------------------- */

	constructor() {}

	/**
	 * Opens a toast notification
	 * @param config The toast configuration
	 * @param timeout Optional timeout to auto-close the toast
	 * @returns A function that can be called to close the toast programmatically
	 */
	public open<T>(config: ToastConfig<T>, timeout = 0): () => void {
		const message: Message = {
			key: 'APP_TOAST',
		};

		config.type = <ToastType>config.type?.toLowerCase() || 'info';
		message.severity = config?.type || 'info';
		Object.assign(message, config);

		// Translate messages
		if (config.message) message.summary = this.translateService.instant(config.message);
		if (config.subMessage) message.detail = this.translateService.instant(config.subMessage);
		if (config.detail) message.detail = this.translateService.instant(config.detail);

		// Set life time based on message type or custom value
		message.life = config.life || this.getDefaultLifeForType(config.type);

		message.styleClass = 'rounded-md font-dc ' + (config.styleClass || '');
		message.styleClass = message.styleClass.trim();
		message.contentStyleClass = 'items-center gap-3 p-2';

		if (config.actionButton) {
			message['actionButton'] = config.actionButton;
		}

		if (this.toastRef) {
			message.closable = config?.closable !== undefined ? config.closable : true;
			this.toastRef.position = config?.position || this.getDefaultPosition();
		}

		message.data = config.data;

		this.messageService.add(message);

		// to close programmatically:
		return () => {
			this.messageService.clear(message.key);
		};
	}

	/**
	 * Handles the action button click in a toast
	 */
	public onClickAction(appToast: Toast, message: ToastConfig): void {
		if (message.actionButton.autoCloseOnAction === true) {
			this.messageService.clear('APP_TOAST');
			appToast['cd'].detectChanges();
		}
		message.actionButton.action(message, appToast, this.injector);
	}

	/**
	 * Shows a success toast with a predefined style
	 * @param message The message key to translate
	 * @param detail Optional detail message key
	 */
	public success(message: string, detail?: string): () => void {
		return this.open({
			type: 'success',
			message,
			subMessage: detail,
			icon: 'pi pi-check-circle',
		});
	}

	/**
	 * Shows an error toast with a predefined style
	 * @param message The message key to translate
	 * @param detail Optional detail message key
	 */
	public error(message: string, detail?: string): () => void {
		return this.open({
			type: 'error',
			message,
			subMessage: detail,
			icon: 'pi pi-times-circle',
			life: ToastService.EXTENDED_TOAST_LIFE,
		});
	}

	/**
	 * Shows a warning toast with a predefined style
	 * @param message The message key to translate
	 * @param detail Optional detail message key
	 */
	public warn(message: string, detail?: string): () => void {
		return this.open({
			type: 'warn',
			message,
			subMessage: detail,
			icon: 'pi pi-exclamation-triangle',
		});
	}

	/**
	 * Get default position based on document direction
	 */
	private getDefaultPosition() {
		return environment.direction === 'rtl' ? 'top-right' : 'top-left';
	}

	/**
	 * Get default life time based on message type
	 */
	private getDefaultLifeForType(type: ToastType): number {
		if (type === 'error') {
			return ToastService.EXTENDED_TOAST_LIFE;
		}
		return ToastService.DEFAULT_TOAST_LIFE;
	}
}
